/**
 * Ordem manual dos cards dentro da coluna do board de Meus deals (card 3307, T10).
 *
 * O backend guarda `boardOrder` (a partir de 1; `0` = nunca reordenado) e devolve a lista já
 * ordenada por ele e, no empate, pelo mais novo. Estas funções repetem essa régua no front para o
 * board reordenar na hora, antes da resposta, e traduzem o índice do drop em posição.
 */

/** O mínimo de um deal que a ordem precisa. */
export interface BoardOrderedDeal {
  id: string;
  boardOrder?: number;
  createdAt: string;
}

/** A régua do backend: `boardOrder` crescente e, no empate, o mais novo primeiro. */
export function compareBoardOrder(a: BoardOrderedDeal, b: BoardOrderedDeal): number {
  return (a.boardOrder ?? 0) - (b.boardOrder ?? 0) || b.createdAt.localeCompare(a.createdAt);
}

/**
 * Posição (a partir de 1) na coluna INTEIRA a partir do índice em que o card caiu na lista
 * EXIBIDA.
 *
 * As duas diferem quando o filtro de parceiro esconde parte da coluna: o card vai para antes do
 * card exibido que ocupava o índice do drop (ou logo depois do último exibido). O índice é o do
 * `ngx-drag-drop`, contado com o próprio card ainda na lista; por isso, na mesma coluna e vindo de
 * cima, ele vale um a menos (mesma conta do Pipeline de Crédito).
 *
 * @param column a coluna inteira, na ordem do board, com o card arrastado se ele já estava nela.
 * @param shown a parte exibida da coluna, na mesma ordem.
 * @param draggedId o card arrastado.
 * @param dropIndex o `event.index` do drop; ausente = fim da coluna.
 */
export function dropPosition(
  column: BoardOrderedDeal[],
  shown: BoardOrderedDeal[],
  draggedId: string,
  dropIndex: number | undefined
): number {
  const fromIndex = shown.findIndex(d => d.id === draggedId);
  let index = dropIndex ?? shown.length;
  if (fromIndex !== -1 && fromIndex < index) index -= 1;

  const shownOthers = shown.filter(d => d.id !== draggedId);
  const columnOthers = column.filter(d => d.id !== draggedId);
  if (!shownOthers.length) return 1;

  const anchor = shownOthers[Math.min(Math.max(index, 0), shownOthers.length)];
  if (anchor) return columnOthers.findIndex(d => d.id === anchor.id) + 1;

  const last = shownOthers[shownOthers.length - 1];
  return columnOthers.findIndex(d => d.id === last.id) + 2;
}

/**
 * A coluna com o card na posição pedida e renumerada de 1 a N, como o backend grava.
 *
 * @param column a coluna de destino na ordem do board, SEM o card arrastado.
 * @param dragged o card arrastado.
 * @param position a posição pedida, a partir de 1; fora da faixa vira o topo ou o fim.
 * @returns id do deal e a posição nova, para a coluna inteira.
 */
export function renumberColumn<T extends BoardOrderedDeal>(
  column: T[],
  dragged: T,
  position: number
): Map<string, number> {
  const ordered = [...column];
  ordered.splice(Math.min(Math.max(position - 1, 0), ordered.length), 0, dragged);
  return new Map(ordered.map((d, i) => [d.id, i + 1]));
}
